import { Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { MessageInput } from '@/components/ui/shared/inputs/MessageInput/MessageInput';
import { TransNextLink } from '@/components/ui/shared/links/TransNextLink/TransNextLink';
import { useAnalytics } from '@/context/Analytics';
import { IToastType } from '@/enums/ToastType';
import { queryLectureCommentsInfinite, createLectureComment } from '@/fetchers/comments';
import useSession from '@/hooks/useSession';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { useMutation, useResourcesInfinite } from '@/lib/datx-jsonapi-react';

interface ICommentRootInputProps {
  lectureId: string;
  threadId?: never;
}

interface ICommentRepliesInputProps {
  lectureId?: never;
  threadId: string;
}

interface ICommentInputOptions {
  onSuccess?: (threadId: string) => void;
}

export type CommentInputProps = (ICommentRootInputProps | ICommentRepliesInputProps) & ICommentInputOptions;

export const CommentInput: FC<CommentInputProps> = ({ lectureId, threadId, onSuccess }) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const router = useRouter();
  const { store } = useStores();
  const toast = useToast();
  const { user } = useSession();

  const { mutate } = useResourcesInfinite(queryLectureCommentsInfinite({ lectureId, threadId }));
  const [create, { status }] = useMutation(createLectureComment, {
    onSuccess: async () => {
      await mutate();
      onSuccess?.(threadId);
    },

    onFailure: () => toast({ status: IToastType.ERROR, title: t('comments.create.error') }),
  });

  const handleSend = async (content: string) => {
    await create({ store, lectureId, threadId, content });
    if (lectureId) {
      analytics.track('library_lecture_comment', { lecture_id: lectureId });
    } else {
      analytics.track('library_lecture_comment_reply_send', { comment_id: threadId });
    }
  };

  if (!user) {
    if (!threadId) {
      return (
        <Text align="center">
          <Trans
            i18nKey="comments.unregisteredUserMessage"
            components={{
              hyperlink: <TransNextLink variant="highlight" href={`/log-in?redirectUrl=${router.asPath}`} />,
            }}
          />
        </Text>
      );
    }

    return null;
  }

  return (
    <MessageInput
      onSend={handleSend}
      placeholder={lectureId ? t('comments.input.placeholder') : t('comments.replies.input.placeholder')}
      bg="white"
      isSubmitting={status === 'running'}
    />
  );
};
